import { Component, OnDestroy, Input, OnInit, Inject, Output, EventEmitter } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';

import { BehaviorSubject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { MenuItem, MessageService } from 'primeng/api';

import { TargetingResultModel } from '../../../../../Discover/Targeting/models/targeting-result.model';
import { TargetingSearchService } from '../../../../../Discover/Targeting/services/targeting-search.service';
import { ButtonTypesEnum } from '../../../../../Common/Forms/enums/button-types.enum';
import { ContactModel } from '../../../../../Common/TargetingShared/model/contact.model';
import { JournodexSubjectParentModel } from '../../../../../Common/Journodex/models/journodex.subject.model';
import { JobRoleModel } from '../../../../../Discover/Core/models/job-role.model';
import { JobRoleTypeEnum } from '../../../../../api/journodex/enum/job-role-type.enum';
import { handleTypeCompare, HandleTypeEnum, handleIconMap } from '../../../../../api/targeting/enum/handle-type.enum';
import { TwitterAvatarSize } from '../../../../../Common/Misc/pipes/TwitterAvatarSize/twitter-avatar-size.pipe';
import { OutletModel } from '../../../../../Discover/Targeting/models/outlet.model';
import { EntityType } from '../../../../../api/targeting/enum/entity.type.enum';
import { HandleModel } from '../../../../../Discover/Targeting/models/handle.model';
import { BaseJournodexEntityModel } from '../../../../../Discover/Targeting/models/base-journodex-entity.model';
import { EmploymentModel } from '../../../../../Discover/Targeting/models/employment.model';
import { DiscoverRoutingService } from '../../../../../Discover/DiscoverRouter/services/discover-routing.service';
import { DiscoverRouteModel } from '../../../../../Discover/DiscoverRouter/models/discover-route.model';
import { ProfileSectionModel } from '../../../../../Discover/Targeting/models/profile-section.model';
import { ProfileItemModel } from '../../../../../Discover/Targeting/models/profile-item.model';
import { LocalizationService } from '../../../../../Core/Localization/services/localization.service';
import { LanguageModel } from '../../../../../Core/Localization/models/language.model';
import { AddressModel, PrivateDataModel } from '../../../../../Discover/Targeting/models/private-data.model';
import { UserModel } from '../../../../../Core/Users/models/user.model';
import { ContactsPublicApi } from '../../../../../api/targeting/contacts-public.api';
import { OutletsPublicApi } from '../../../../../api/targeting/outlets-public.api';
import { messageSeverityEnum } from '../../../../../Common/Misc/enums/message-severity.enum';
import { TargetingRhpContextsService } from '../../../../../Common/TargetingShared/services/targeting-rhp-contexts.service';
import { ContactService } from '../../../../../Common/TargetingShared/services/contact-service';
import { JournodexGeographyModel } from '../../../../../Common/Journodex/models/journodex-geography.model';
import { ProfileItemDisplayObject } from '../../../../../Discover/Targeting/models/profile-item.model';
import { MediaMarketsDisplayPipe } from '../../../../Misc/pipes/MediaMarketsDisplay/media-markets-display.pipe';
import { ConfigService } from '../../../../../Core/Config/services/config.service';
import { UserNoteModel } from '../../../../../Common/Misc/models/user-note.model';
import { FullProfileAnimationModel } from '@Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '@Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '@Core/Dialogs/enums/overlay-animation.enum';
import { AddPrivateDataDialogComponent } from '@Discover/Targeting/components/TargetingResults/AddPrivateDataDialog/add-private-data-dialog.component';
import { ManageListDialogComponent } from '@Discover/Targeting/components/TargetingResults/ManageListDialog/manage-list-dialog.component';
import { ResearchRequestDialogComponent } from '@Common/Dialogs/components/ResearchRequestDialog/research-request-dialog.component';
import { LivenessEnum } from '@api/targeting/enum/liveness.enum';
import { CoreDialogService } from '@Core/Dialogs/services/dialog.service';
@Component({
    selector: 'agpr-entity-profile-details',
    templateUrl: './entity-profile-details.component.html',
    styleUrls: ['./entity-profile-details.component.scss'],
    providers: [MediaMarketsDisplayPipe],
    standalone: false
})
export class EntityProfileDetailsComponent implements OnInit, OnDestroy {
    @Input() id: string;
    @Input() contactPrivateData: PrivateDataModel;
    @Input() outletPrivateData: PrivateDataModel;
    @Input() set entity(entity: BaseJournodexEntityModel) {
        if (!entity) {
            return;
        }

        this.fullEntity = entity;
        this.contactEntity = <ContactModel>entity;
        this.outletEntity = <OutletModel>entity;
        this.entityType = entity.entityType;
        this.actions = [
            {
                label: 'Discover.addPrivateData.buttonLabel',
                command: () => this.openAddPrivateDataDialog()
            },
            {
                label: 'Discover.manageList.header',
                command: () => this.openManageListDialog()
            }
        ];
        if (entity?.entityType === EntityType.CONTACT) {
            if (!!this.contactEntity.privateContactMetadata) {
                this.actions.splice(0, 1);
                this.actions.splice(0, 0, {
                    label: 'Discover.targeting.actions.edit',
                    command: () => this.openContactEditor()
                });
            } else {
                this.actions.splice(0, 0, {
                    label: 'Discover.research.researchRequest',
                    command: () => this.openResearchRequestDialog()
                });
            }
        } else {
            if (!entity.id || (<OutletModel>entity).liveness !== LivenessEnum.ACTIVE) {
                this.actions.splice(0, 1);
            }
            this.actions.splice(0, 0, {
                label: this.localizationService.transform('Discover.research.researchRequest'),
                command: () => this.openResearchRequestDialog()
            });
        }
        this.hasSystemAddresses = !!this.outletEntity.addressLine1 || !!this.outletEntity.addressLine2
            || !!this.outletEntity.geography || !!this.outletEntity.postalCode;

        if (this.fullEntity) {
            this.subjects = this.fullEntity.subjects;
            if (this.fullEntity.entityType === this.entityTypes.CONTACT) {
                this.hasContactAddresses = !!this.contactEntity.addressLine1 || !!this.contactEntity.addressLine2
                    || !!this.contactEntity.geography || !!this.contactEntity.postalCode;
            }
        }

        this.isGeoRestricted = this.fullEntity.isGeoRestricted;

        // Contacts can have some employments that are restricted and others that aren't
        // so we can't just rely on the top level field since it changes what info will
        // be shown in the RHP.
        if (this.contactEntity?.selectedEmployment?.outlet) {
            this.isGeoRestricted = this.contactEntity.selectedEmployment.outlet.isGeoRestricted;
        }


        this.setOutletProfileSections();
        this.languages = this.fullEntity?.languages;
        this.organizeHandles();

        if (this._selectedEmployment) {
            this.setSelectedEmployment();
        }
    }
    @Input() set privateData(privateData: PrivateDataModel) {
        this._privateData = privateData;
        if (privateData) {
            if (this.fullEntity?.entityType === this.entityTypes.CONTACT) {
                this.contactPrivateAddresses = privateData.addresses;
            }
        }
        this.organizeHandles();
    }

    @Input() outletPrivateAddresses: AddressModel[];

    @Input() set selectedEmployment(employmentModel: EmploymentModel) {
        this._selectedEmployment = employmentModel;

        if (!this.contactEntity || !this._selectedEmployment) {
            return;
        }
        this.setSelectedEmployment();
    }
    @Input() currentUser: UserModel;
    @Input() dialogMode: boolean;
    @Output() showContactEditor: EventEmitter<void> = new EventEmitter();


    searchEntity: TargetingResultModel;
    fullEntity: BaseJournodexEntityModel;
    outletEntity: OutletModel;
    contactEntity: ContactModel;
    _privateData: PrivateDataModel;
    contactPrivateAddresses: AddressModel[] = [];
    hasSystemAddresses: boolean = false;
    hasContactAddresses: boolean = false;
    privateAddresses: AddressModel[] = [];
    _selectedEmployment: EmploymentModel;
    userNotes: UserNoteModel[] = [];
    isTruncated: boolean = false;
    actions: MenuItem[] = [];

    currentRoute: DiscoverRouteModel;
    entityType: EntityType;
    entityFullProfilePath: string;

    subjects: JournodexSubjectParentModel[];
    roleFamilies: {
        family: JobRoleTypeEnum;
        roles: JobRoleModel[];
    }[];
    languages: LanguageModel[];
    handleTypes: {
        type: HandleTypeEnum;
        tooltip: string;
        handles: HandleModel[];
    }[];

    outletProductSection: ProfileSectionModel;
    coverageProfileSection: ProfileSectionModel;
    hasPreferredEmail = false;
    isGeoRestricted: boolean;

    showSection: boolean = true;

    contactModel: ContactModel;
    newSelectedEmploymentId: number;

    similarWebTooltip: string;

    readonly buttonTypes = ButtonTypesEnum;
    readonly twitterAvatarSizes = TwitterAvatarSize;
    readonly entityTypes = EntityType;
    readonly handleTypeEnum = HandleTypeEnum;
    readonly handleTypeIcons = handleIconMap;

    private subscriptions: Subscription[] = [];

    constructor(
        public targetingSearchService: TargetingSearchService,
        public discoverRoutingService: DiscoverRoutingService,
        public localizationService: LocalizationService,
        public contactsPublicApi: ContactsPublicApi,
        public outletsPublicApi: OutletsPublicApi,
        public messageService: MessageService,
        public targetingRhpContextsService: TargetingRhpContextsService,
        public contactService: ContactService,
        public mediaMarketsDisplayPipe: MediaMarketsDisplayPipe,
        public configService: ConfigService,
        private coreDialogService: CoreDialogService,
        @Inject(APP_BASE_HREF) public appBaseHref: string
    ) {
        if (this.configService.brandGUIDIsRefMedia()) {
            this.showSection = false;
        }

        this.subscriptions.push(
            this.discoverRoutingService.getCurrentRoute().subscribe(route => {
                this.currentRoute = route;
            }),
            this.contactService.getPrivateDataAllLayerUpdate().pipe(filter(x => !!x)).subscribe(update => {
                if (update.entityId === this.fullEntity?.id) {
                    this._privateData = update.privateData;
                }
                this.organizeHandles();
            }),
            this.contactService.getHandlesAllLayerUpdate().pipe(filter(x => !!x)).subscribe(update => {
                if (update.entityType === this.entityType) {
                    if (update.entityType === EntityType.CONTACT && update.entityId === this.contactEntity?.id) {
                        this.contactEntity.handles = update.handles;
                    }
                    if (update.entityType === EntityType.OUTLET && update.entityId === this.outletEntity?.id) {
                        this.outletEntity.handles = update.handles;
                    }
                    this.organizeHandles();
                }
            })
        );

        this.similarWebTooltip = this.localizationService.transform(
            'Common.audienceReach.similarWeb.tooltip',
            {src: this.appBaseHref + 'assets/images/SimilarWeb-logo-with-text.svg'}
        );
    }

    ngOnInit() {
        if (this.entityType === EntityType.CONTACT && this.contactPrivateData?.addresses) {
            this.privateAddresses = this.contactPrivateData.addresses;
        } else if (this.entityType === EntityType.OUTLET && this.outletPrivateData?.addresses) {
            this.privateAddresses = this.outletPrivateData.addresses;
        }
    }

    openAddPrivateDataDialog() {
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        const overlayContainer = document.getElementById('main-content');
        this.coreDialogService.open(AddPrivateDataDialogComponent, {
            inputs: {
                setEntity: (this.fullEntity.entityType === EntityType.CONTACT ? this.contactEntity : this.outletEntity),
                setPrivateData: this._privateData,
                currentUser: this.currentUser,
                overlayContainer: overlayContainer,
                animationTrigger: animationTrigger
            },
            outputs: {}
        });
    }

    openManageListDialog() {
        this.coreDialogService.open(
            ManageListDialogComponent,
            {
                inputs: {
                    setEntity: this.fullEntity.entityType === EntityType.CONTACT ?
                        this.contactEntity : this.outletEntity,
                },
                outputs: {
                }
            }
        );
    }


    openContactEditor() {
        this.contactService.setEditingContact(this.contactEntity.id);
        // TODO : Ideally we should update the URL but not lose the "context"
        // so that the user can share the page but still be able to close the overlay and return
        // to his original context
        this.showContactEditor.emit();
    }

    openResearchRequestDialog() {
        const inputs = this.fullEntity.entityType === EntityType.CONTACT
            ? { contact: this.contactEntity }
            : { outlet: this.outletEntity };
        this.coreDialogService.open(ResearchRequestDialogComponent, { inputs: inputs });
    }

    checkTruncatedText(textElement: HTMLElement): void {
        this.isTruncated = textElement.scrollWidth > textElement.clientWidth;
    }

    setOutletProfileSections(): void {
        if (this.fullEntity.entityType !== EntityType.OUTLET) {
            this.outletProductSection = null;
            this.coverageProfileSection = null;

            return;
        }

        this.outletProductSection = new ProfileSectionModel(
            'Discover.entityProfile.productsAndAudience',
            [
                new ProfileItemModel(
                    'Discover.entityProfile.mediaType',
                    this.getLocalizedListDisplayString(this.outletEntity.mediaTypes)
                ),
                new ProfileItemModel(
                    'Discover.entityProfile.productType',
                    this.outletEntity.productType?.displayString
                ),
                new ProfileItemModel(
                    'Discover.entityProfile.productFrequency',
                    this.outletEntity.productFrequency?.displayString
                ),
                new ProfileItemModel(
                    'Discover.entityProfile.marketSector',
                    this.outletEntity.marketSector?.displayString
                ),
                new ProfileItemModel(
                    'Discover.entityProfile.targetAudiences',
                    this.getLocalizedListDisplayString(this.outletEntity.targetAudiences)
                )
            ]
        );

        this.coverageProfileSection = new ProfileSectionModel(
            'Discover.entityProfile.coverageProfile',
            [
                new ProfileItemModel(
                    'Discover.entityProfile.coverageFocus',
                    this.outletEntity.coverageFocus?.displayString
                ),
                new ProfileItemModel(
                    'Discover.entityProfile.targetAudienceRegion',
                    this.getTargetAudienceRegionsDisplayObject(this.outletEntity.targetAudienceRegions)
                ),
                new ProfileItemModel(
                    'Discover.entityProfile.mediaMarkets',
                    this.mediaMarketsDisplayPipe.transform(this.outletEntity.mediaMarkets)
                ),
                new ProfileItemModel(
                    'Discover.entityProfile.language',
                    this.getLocalizedListDisplayString(this.outletEntity.languages, 'name')
                )
            ]
        );
    }

    /**
     * Returns a string concatenated of localized tokens
     */
    getLocalizedListDisplayString(tokenObjects: any[], tokenProperty: string = 'displayString'): string {
        const localizedModels = <any>this.localizationService.transformList(tokenObjects, [], tokenProperty);

        return localizedModels.map(localizedEnum => localizedEnum[tokenProperty]).join(', ');
    }


    getTargetAudienceRegionsDisplayObject(regions: JournodexGeographyModel[]): ProfileItemDisplayObject[] {
        const displayArray = [];
        if (regions.length > 0) {
            displayArray.push({content: regions.map(x => x.keyString)});
        }
        return displayArray;
    }

    setSelectedEmployment() {
        this.contactEntity.selectedEmployment = this._selectedEmployment;
        this.outletEntity = this._selectedEmployment.outlet;
        this.hasSystemAddresses = !!this.outletEntity && (!!this.outletEntity.addressLine1 || !!this.outletEntity.addressLine2
            || !!this.outletEntity.geography || !!this.outletEntity.postalCode);
        this.organizeJobRoles();
    }

    organizeJobRoles(): void {
        if (
            !this._selectedEmployment
            || !this._selectedEmployment.jobRoles
            || this._selectedEmployment.jobRoles.length < 1
        ) {
            this.roleFamilies = null;
            return;
        }

        const rolesMap: Map<JobRoleTypeEnum, JobRoleModel[]> = new Map<JobRoleTypeEnum, JobRoleModel[]>();

        this._selectedEmployment.jobRoles.forEach(role => {
            if (!rolesMap.has(role.family)) {
                rolesMap.set(role.family, []);
            }
            rolesMap.get(role.family).push(role);
        });

        this.roleFamilies = Array.from(rolesMap.entries()).map(([family, roles]) => {
            return {
                family,
                roles
            };
        });
    }

    organizeHandles(): void {
        this.hasPreferredEmail = false;

        if (!this.fullEntity || !this.fullEntity.handles || this.fullEntity.handles.length < 1) {
            this.handleTypes = null;
            return;
        }

        const handlesMap: Map<HandleTypeEnum, HandleModel[]> = new Map<HandleTypeEnum, HandleModel[]>();
        this.fullEntity.handles.forEach(handle => {
            if (!handlesMap.has(handle.handleType)) {
                handlesMap.set(handle.handleType, []);
            }
            handlesMap.get(handle.handleType).push(handle);
        });

        this.handleTypes = [];
        for (const handleType of Object.keys(this.handleTypeIcons) as HandleTypeEnum[]) {
            let handles: HandleModel[] = [];
            if (handlesMap.has(handleType)) {
                handles = handlesMap.get(handleType);
            }

            if (this._privateData) {
                this._privateData.handles.filter(handle => handle.handleType === handleType).forEach(handle => {
                    handles.push(handle);
                });
            }

            if (handles.length > 0) {
                if (handleType === HandleTypeEnum.EMAIL && !!handles.find(x => x.preferred)) {
                    this.hasPreferredEmail = true;
                }

                handles.sort((x1, x2) => {
                    if (x1.preferred) {
                        return -1;
                    }
                    if (x1.primary && !x2.preferred) {
                        return -1;
                    }
                    return 0;
                });

                this.handleTypes.push({
                    type: handleType,
                    tooltip: `Common.handleTypes.${handleType}`,
                    handles: handles
                });
            }
        }

        // We want to show a contact's employment outlet's phone handles if the contact has none of their own
        if (this.fullEntity.entityType === EntityType.CONTACT) {
            const phoneHandles = handlesMap.get(HandleTypeEnum.PHONE);
            if (!(phoneHandles?.length)) {
                const outletPhoneHandles = this.contactEntity.selectedEmployment?.outlet?.handles?.filter(handle => handle.handleType === HandleTypeEnum.PHONE);
                if (outletPhoneHandles?.length) {
                    this.handleTypes.push({
                        type: HandleTypeEnum.PHONE,
                        tooltip: `Common.handleTypes.${HandleTypeEnum.PHONE}`,
                        handles: [...outletPhoneHandles.map(handle => {
                            const apiModel = handle.toApiModel();
                            const officeHandle = new HandleModel(apiModel);
                            officeHandle.value += ' (Office)';
                            return officeHandle;
                        })]
                    });
                }
            }
        }

        this.handleTypes.sort(handleTypeCompare);
    }

    setPreferred(handle: HandleModel) {
        let apiCall;
        if (this.fullEntity.entityType === EntityType.CONTACT) {
            apiCall = this.contactsPublicApi.setPreferredEmail(this.fullEntity.id, handle);
        } else if (this.fullEntity.entityType === EntityType.OUTLET) {
            apiCall = this.outletsPublicApi.setPreferredEmail(this.fullEntity.id, handle);
        }
        if (apiCall) {
            this.hasPreferredEmail = true;
            apiCall.subscribe(done => {
                this.handleTypes.filter(x => x.type === HandleTypeEnum.EMAIL)
                    .map(x => x.handles)
                    .filter(emails => {
                        emails.forEach(email => {
                            email.preferred = email === handle;
                            // setting the primary as preferred is a "clearing" operation since the primary cannot be preferred
                            if (email.primary && email === handle) {
                                email.preferred = false;
                                this.hasPreferredEmail = false;
                            }
                        });
                    });
                const updatedPrivateHandles = [];
                const updatedHandles = [];
                this.handleTypes.forEach(handleType => {
                    handleType.handles.sort((x1, x2) => {
                        if (x1.preferred) {
                            return -1;
                        }
                        if (x1.primary && !x2.preferred) {
                            return -1;
                        }
                        return 0;
                    });
                    updatedPrivateHandles.push(...handleType.handles.filter(x => x.isPrivateData));
                    updatedHandles.push(...handleType.handles.filter(x => !x.isPrivateData));
                });

                this._privateData.handles = updatedPrivateHandles;
                this.contactService.setHandlesAllLayerUpdate({
                    entityId: this.fullEntity.id,
                    entityType: this.fullEntity.entityType,
                    handles: updatedHandles
                });
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: 'Discover.entityProfile.handle.preferred.success.summary',
                    detail: this.localizationService.transform('Discover.entityProfile.handle.preferred.success', { email: handle.value})
                });
            });
        } else {
            throw new Error('Should never get here');
        }
    }


    navigateToContactUs(): void {
        if (this.configService.brandGUIDIsBurrelles()) {
            window.location.href = 'mailto:inquiry@burrelles.com?subject=Sales%20Inquiry%20-%20Burrelles%20Contact%20Database';
        } else {
            window.open('https://www.agilitypr.com/contact/');
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
